import { useDisclosure } from '@mantine/hooks'
import { ShieldOutlined } from '@mui/icons-material'
import {
  alpha,
  Box,
  Button,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import { FormikProvider } from 'formik'
import { isEmpty } from 'lodash'
import {
  ForwardedRef,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import { PopupModal } from 'react-calendly'
import { useLocation } from 'react-router-dom'

import { ENTER_KEY } from '~/constants'
import {
  findCustomerEmail,
  formatMessages,
} from '~/features/chatbot/components/dialog/dialogInput/utils'
import { CALENDLY_PORTAL_ID } from '~/features/chatbot/constants'
import useChatSource from '~/features/chatbot/hooks/useChatSource'
import { getChatbotAccentColor } from '~/features/chatbot/utils'
import PreChatForm from '~/features/chatbotV2/components/dialog/dialogInput/preChatForm'
import SendButton from '~/features/chatbotV2/components/dialog/dialogInput/sendButton'
import { TChatV2Params } from '~/features/chatbotV2/hooks/useChatV2'
import usePreChatForm from '~/features/chatbotV2/hooks/usePreChatForm'
import { CALENDLY_REGEX } from '~/features/settings/constants'
import useIdentifyViewportWidth from '~/features/ui/hooks/useIdentifyViewportWidth'
import EElement from '~/features/ui/models'
import { store } from '~/store'
import rem from '~/utils/ui'

interface TProps {
  mutate: (params: TChatV2Params) => void
  isPending: boolean
  hasSessionEnded: boolean
  guestToken: string | null
  refreshGuestToken: () => Promise<string | null>
  isSessionReady: boolean
  sendTyping: () => void
}

// Clone of features/chatbot/components/dialog/dialogInput (v1). mutate/
// isPending come in as props (see components/simple for why) instead of
// calling useChat(). Deliberately drops v1's client-driven inactivity
// warning/auto-end (useEndChatbotConversation + timers) — v2's chat-service
// backend already runs its own 10-min auto-end + 1-min warning server-side
// (SESSION_WARNING/SESSION_ENDED events), so no client timer is needed here.
// hasSessionEnded mirrors v1's hasConversationEnded — once SESSION_ENDED
// arrives, the guest must not be able to reply, same as v1.
const DialogInput = forwardRef(
  (
    {
      mutate,
      isPending,
      hasSessionEnded,
      guestToken,
      refreshGuestToken,
      isSessionReady,
      sendTyping,
    }: TProps,
    dialogInputWrapperRef: ForwardedRef<HTMLDivElement>,
  ) => {
    const { isExtraSmallScreen, isSmallScreen } = useIdentifyViewportWidth()
    const [opened, { open, close }] = useDisclosure(false)
    const location = useLocation()
    const isTestMode = useMemo(
      () => new URLSearchParams(location.search).get('istesting') === 'true',
      [location.search],
    )
    const [chatbotLayout, showBookAMeeting, messages, chatbotAgent] = store(
      (state) => [
        state.chatbot.layout,
        state.chatbot.showBookAMeeting,
        state.chatbot.messages,
        state.chatbot.agent,
      ],
    )
    const theme = useTheme()
    const inputRef = useRef<HTMLInputElement | null>(null)
    const selectedTheme = chatbotLayout.selection.theme
    const { accentColor } = getChatbotAccentColor({
      layout: chatbotLayout,
      selectedTheme,
    })
    const { isUserLoading, source } = useChatSource()

    const {
      formik: preChatFormik,
      isPreChatFormSendDisabled,
      isPreChatFormVisible,
      isSubmittingPreChatForm,
      sortedFields: preChatFormSortedFields,
    } = usePreChatForm({ guestToken, isSessionReady, refreshGuestToken })

    const isChatInputDisabled = isPending || isUserLoading || hasSessionEnded
    const isSendDisabled = isChatInputDisabled || isPreChatFormSendDisabled

    // Focus after chat is pending but NOT on mobile
    useEffect(() => {
      if (
        !isPending &&
        inputRef.current &&
        !(isExtraSmallScreen || isSmallScreen)
      ) {
        inputRef.current.focus()
      }
    }, [isPending, isExtraSmallScreen, isSmallScreen])

    const sendChatMessage = useCallback(() => {
      if (!inputRef.current || isEmpty(inputRef.current.value)) {
        return false
      }

      const sent = chatbotAgent.id
        ? mutate({
            agentId: chatbotAgent.id,
            message: inputRef.current.value,
            ...(source && { source }),
            ...(isTestMode && { type: 'test' }),
          })
        : false

      if (sent && inputRef.current) {
        inputRef.current.value = ''
        inputRef.current.focus()
      }

      return sent
    }, [chatbotAgent.id, isTestMode, mutate, source])

    const handleComposerSend = useCallback(async () => {
      if (isSendDisabled) {
        return
      }

      if (isPreChatFormVisible) {
        try {
          const submitErrors = await preChatFormik.submitForm()

          if (submitErrors) {
            return
          }
        } catch {
          return
        }
      }

      sendChatMessage()
    }, [isPreChatFormVisible, isSendDisabled, preChatFormik, sendChatMessage])

    const handleMessageKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === ENTER_KEY) {
          event.preventDefault()
        }

        if (event.code !== ENTER_KEY && event.key !== ENTER_KEY) {
          return
        }

        if (isSendDisabled) {
          return
        }

        const target = event.target as HTMLInputElement

        if (isEmpty(target.value)) {
          return
        }

        handleComposerSend()
      },
      [handleComposerSend, isSendDisabled],
    )

    const handlePreChatFieldEnterKeyDown = useCallback(() => {
      handleComposerSend()
    }, [handleComposerSend])

    // Fires on every keystroke — sendTyping itself throttles the actual send
    // (see useChatV2), so no debounce is needed here. Not sent while the
    // guest is mid-send (isPending) or once the session has ended — nothing
    // useful for staff to see at that point.
    const handleChange = () => {
      if (isPending || hasSessionEnded) return
      sendTyping()
    }

    return (
      <>
        <Box
          ref={dialogInputWrapperRef}
          id={EElement.chatbotMessageTextfield}
          sx={{
            width: '100%',
            padding: rem(12),
            backgroundColor: {
              xxxs: theme.palette.common.white,
              md: alpha(theme.palette.common.white, 0),
            },
            paddingTop: 0,
          }}
        >
          <Stack
            sx={{
              gap: 1.5,
            }}
          >
            {!isEmpty(chatbotAgent.calendlyLink) && showBookAMeeting && (
              <Box
                sx={{
                  display: 'flex',
                }}
              >
                <Stack gap={1}>
                  <Button
                    onClick={() =>
                      CALENDLY_REGEX.test(chatbotAgent.calendlyLink ?? '')
                        ? open()
                        : window.open(chatbotAgent.calendlyLink, '_blank')
                    }
                    variant="secondary"
                  >
                    Book a Meeting
                  </Button>
                </Stack>
              </Box>
            )}

            <FormikProvider value={preChatFormik}>
              <Stack useFlexGap>
                {isPreChatFormVisible && (
                  <PreChatForm
                    onFieldEnterKeyDown={handlePreChatFieldEnterKeyDown}
                    sortedFields={preChatFormSortedFields}
                  />
                )}

                <TextField
                  maxRows={2}
                  multiline
                  inputRef={inputRef}
                  disabled={isChatInputDisabled}
                  placeholder="Type your response..."
                  hiddenLabel
                  variant="standard"
                  onKeyDown={handleMessageKeyDown}
                  onChange={handleChange}
                  InputProps={{
                    disableUnderline: true,
                    onFocus: () => {
                      const isMobileSafari =
                        /iPad|iPhone|iPod/.test(navigator.userAgent) &&
                        /Safari/.test(navigator.userAgent)

                      if (!isMobileSafari) return

                      setTimeout(() => {
                        document.activeElement?.scrollIntoView({
                          behavior: 'smooth',
                        })
                      }, 300)
                    },
                    endAdornment: (
                      <SendButton
                        accentColor={accentColor}
                        isPreChatFormVisible={isPreChatFormVisible}
                        isSendDisabled={isSendDisabled}
                        isSubmittingPreChatForm={isSubmittingPreChatForm}
                        onSendClick={handleComposerSend}
                      />
                    ),
                  }}
                  sx={{
                    paddingRight: rem(8),
                    paddingLeft: rem(16),
                    flexGrow: 1,
                    backgroundColor: theme.palette.common.white,
                    border: `${rem(1)} solid ${theme.palette.divider}`,
                    ...(isPreChatFormVisible
                      ? {
                          height: rem(80),
                          paddingTop: rem(8),
                          borderRadius: rem(12),
                          borderTopLeftRadius: 0,
                          borderTopRightRadius: 0,
                          '> .MuiInputBase-root': {
                            height: '100%',
                            alignItems: 'flex-start',
                            '.MuiIconButton-root': {
                              marginTop: 'auto',
                            },
                          },
                        }
                      : {
                          borderRadius: rem(48),
                        }),
                    '& .MuiInputBase-input.MuiInput-input': {
                      color: theme.palette.mainFont,
                    },
                    '& .MuiInputBase-input.Mui-disabled': {
                      WebkitTextFillColor: theme.palette.tertiary4,
                    },
                  }}
                />
              </Stack>
            </FormikProvider>

            {/* No bubble/background, no dismiss — spec'd to "modernize it &
                then we don't need the x button & it can take up less
                space", so this is permanent chrome, not a one-time toast. */}
            <Stack
              direction="row"
              gap={0.75}
              sx={{
                alignItems: 'flex-start',
                paddingInline: rem(4),
              }}
            >
              <ShieldOutlined
                sx={{
                  fontSize: rem(14),
                  color: theme.palette.tertiary4,
                  marginTop: rem(1),
                }}
              />
              <Typography
                sx={{
                  fontSize: rem(12),
                  color: theme.palette.tertiary4,
                }}
              >
                Powered by AI and designed to provide accurate information, but
                always verify important details with {chatbotAgent.businessName}
                .
              </Typography>
            </Stack>
          </Stack>
        </Box>
        <PopupModal
          open={opened}
          onModalClose={close}
          url={`${chatbotAgent.calendlyLink}?email=${encodeURIComponent(findCustomerEmail(messages) ?? '')}&a1=${encodeURIComponent(formatMessages(messages, chatbotAgent.agentName))}`}
          rootElement={
            document.getElementById(CALENDLY_PORTAL_ID) as HTMLElement
          }
        />
      </>
    )
  },
)

export default DialogInput
